import { format } from 'date-fns';
import React, { ReactNode } from 'react';
import { isChrome, isIOS, isMobile, isTablet } from 'react-device-detect';
import { BroadcastPlayerAsset, PlayerControl, PlayingAsset, RecordingPlayerAssetInfo } from 'types/Player';
import { StyledSVGInline } from 'components/PlayerUI/PlayerUI.css';
import icons from 'style';
import { PlayerTrackSelector, PlayerTrackSelectorMobile } from 'components/PlayerTrackSelector/PlayerTrackSelector';
import { PlayerEpisodeSelector, PlayerEpisodeSelectorMobile } from 'components/PlayerEpisodeSelector/PlayerEpisodeSelector';
import ApplicationConfig from 'providers/useConfig/ApplicationConfig';
import { CastAssetType } from 'types/Cast';
import { AspectRatio } from 'types/Common';
import { isCatchupPlayerAsset, isLivePlayerAsset, isRecordingPlayerAsset, isVodPlayerAsset } from './fnTypeGuards';
import { TIME_FORMAT_HH_MM } from './fnParser';
import { formatDate } from './fnDate';
import { joinMetaDataValues } from './fnMetaData';

export const getBroadcastPlayerMetaData = (asset: BroadcastPlayerAsset | RecordingPlayerAssetInfo) => {
    if (!asset) return [];

    const { startTime, endTime, releaseYear, countries, genres } = asset;
    const metaData = [];

    if (startTime && endTime) {
        metaData.push(formatDate(new Date(startTime)));
        metaData.push(`${format(startTime, TIME_FORMAT_HH_MM)} - ${format(endTime, TIME_FORMAT_HH_MM)}`);
    }

    if (releaseYear || countries?.length) {
        metaData.push(joinMetaDataValues(countries?.slice(0, 2).join(','), releaseYear));
    }

    if (genres?.length) {
        metaData.push(
            genres
                ?.map((genre) => genre.Value)
                .slice(0, 2)
                .join(', ')
        );
    }

    return metaData;
};

export const getBroadcastPlayerMoreOverlayMetaData = (asset) => {
    const metaData = {
        firstLine: [],
        secondLine: [],
    };

    if (!asset) return metaData;

    const { startTime, endTime, releaseYear, countries, genres, channelName } = asset;

    if (channelName) {
        metaData.firstLine.push(channelName);
    }

    if (startTime && endTime) {
        metaData.firstLine.push(formatDate(new Date(startTime)));
        metaData.firstLine.push(`${format(startTime, TIME_FORMAT_HH_MM)} - ${format(endTime, TIME_FORMAT_HH_MM)}`);
    }

    if (releaseYear || countries?.length) {
        metaData.secondLine.push(joinMetaDataValues(countries?.slice(0, 2).join(','), releaseYear));
    }

    if (genres?.length) {
        metaData.secondLine.push(
            genres
                ?.map((genre) => genre.Value)
                .slice(0, 2)
                .join(', ')
        );
    }

    return metaData;
};

export const isSeriesPlayerAsset = (asset: PlayingAsset): boolean => {
    if (isVodPlayerAsset(asset)) {
        return !asset.isMovie && !!asset.vodSeriesId;
    }

    if (isLivePlayerAsset(asset) || isCatchupPlayerAsset(asset) || isRecordingPlayerAsset(asset)) {
        return !!asset.seriesId;
    }

    return false;
};

export const getTrailerVODPlayerControls = (hasTracks: boolean, isCastEnabled: boolean) => {
    const controls: PlayerControl[] = [PlayerControl.FULL_SCREEN, PlayerControl.VOLUME];

    if (isCastEnabled) {
        controls.push(PlayerControl.CAST);
    }

    controls.push(PlayerControl.INFO);

    controls.push(PlayerControl.SUBTITLE);

    if (!isMobile && !isTablet) {
        controls.push(PlayerControl.JUMP_BACK);
        controls.push(PlayerControl.JUMP_FORWARD);
    }
    controls.push(PlayerControl.PLAY_PAUSE);

    return controls;
};

export const getBroadcastPlayerActionComponents = (controls: PlayerControl[]) => {
    const mobileOverlayActionHandlers = [];
    const actionPopup: {
        [key in PlayerControl]?: ReactNode;
    } = {};

    if (controls.includes(PlayerControl.SUBTITLE)) {
        mobileOverlayActionHandlers.push(<PlayerTrackSelectorMobile key={PlayerControl.SUBTITLE} />);
        actionPopup[PlayerControl.SUBTITLE] = <PlayerTrackSelector triggerButton={<StyledSVGInline svg={icons.icoSettings} />} />;
    }

    if (controls.includes(PlayerControl.EPISODES)) {
        mobileOverlayActionHandlers.push(<PlayerEpisodeSelectorMobile key={PlayerControl.EPISODES} />);
        actionPopup[PlayerControl.EPISODES] = <PlayerEpisodeSelector triggerButton={<StyledSVGInline svg={icons.moreEpisodes} />} />;
    }

    return {
        mobileOverlayActionHandlers,
        actionPopup,
    };
};

export const getTrailerVODPlayerActionComponents = (controls: PlayerControl[]) => {
    const mobileOverlayActionHandlers = [];
    const actionPopup: {
        [key in PlayerControl]?: ReactNode;
    } = {};

    if (controls.includes(PlayerControl.SUBTITLE)) {
        mobileOverlayActionHandlers.push(<PlayerTrackSelectorMobile key={PlayerControl.SUBTITLE} />);
        actionPopup[PlayerControl.SUBTITLE] = <PlayerTrackSelector triggerButton={<StyledSVGInline svg={icons.icoSettings} />} />;
    }

    if (controls.includes(PlayerControl.EPISODES)) {
        mobileOverlayActionHandlers.push(<PlayerEpisodeSelectorMobile key={PlayerControl.EPISODES} />);
        actionPopup[PlayerControl.EPISODES] = <PlayerEpisodeSelector triggerButton={<StyledSVGInline svg={icons.moreEpisodes} />} />;
    }

    return {
        mobileOverlayActionHandlers,
        actionPopup,
    };
};

export const castAssetType = (asset: PlayingAsset): CastAssetType => {
    if (isLivePlayerAsset(asset)) {
        return 'LIVE';
    }

    if (isRecordingPlayerAsset(asset)) {
        return 'RECORDING';
    }

    if (isCatchupPlayerAsset(asset)) {
        return 'REPLAY';
    }

    if (isVodPlayerAsset(asset)) {
        return 'VOD';
    }

    return 'VOD_TRAILER';
};

const castCapable = (): boolean => isChrome && !isIOS;

export const castEnabled = () => castCapable() && ApplicationConfig && ApplicationConfig.isFeatureActive('google_cast');

export const setVideoAspectRatio = (video: HTMLVideoElement, videoAspectRatio: AspectRatio, wrapper?: HTMLDivElement) => {
    if (!video) {
        return;
    }

    const viewportWidth = wrapper?.clientWidth ?? window.innerWidth;
    const viewportHeight = wrapper?.clientHeight ?? window.innerHeight;
    const neededAspectRatio = videoAspectRatio.width / videoAspectRatio.height;
    const screenRatio = viewportWidth / viewportHeight;
    const applyCoeff = screenRatio / neededAspectRatio;
    if (applyCoeff >= 1) {
        // dimension.width >= dimension.height
        video.height = viewportHeight;
        video.width = viewportHeight * neededAspectRatio;
    } else {
        // dimension.width < dimension.height
        video.width = viewportWidth;
        video.height = viewportWidth / neededAspectRatio;
    }
};
